import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Cloud, CheckCircle2, XCircle, Loader2, Save, AlertTriangle, KeyRound } from "lucide-react";
import { toast } from "sonner";
import CheckQualityConfig from "../components/CheckQualityConfig";
import TrasfertaConfig from "../components/TrasfertaConfig";
import DicoImpresaConfig from "../components/DicoImpresaConfig";
import EmailArubaConfig from "../components/EmailArubaConfig";

const BLANK = {
  base_url: "",
  username: "",
  password: "",
  folder_root: "",
  enabled: true,
  auto_upload: true,
};

export default function Impostazioni() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [serverState, setServerState] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/nextcloud");
      setServerState(data);
      setForm({
        base_url: data.base_url || "",
        username: data.username || "",
        password: "", // non ricevuta mai dal server
        folder_root: data.folder_root || "",
        enabled: data.enabled,
        auto_upload: data.auto_upload,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setTestResult(null);
    try {
      const { data } = await api.put("/settings/nextcloud", form);
      setServerState(data);
      setForm({ ...form, password: "" });
      toast.success("Configurazione salvata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/nextcloud/test");
      setTestResult(data);
      if (data.ok) toast.success("Connessione riuscita");
      else toast.error(data.message);
    } catch (err) {
      setTestResult({ ok: false, message: formatApiError(err) });
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  }

  return (
    <div data-testid="impostazioni-page" className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Impostazioni</h1>
        <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
          Configura le integrazioni esterne del tuo CRM
        </p>
      </div>

      <div className="card-shell overflow-hidden">
        <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#E8F3EB", color: "var(--primary)" }}>
            <Cloud className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Aruba Drive / Nextcloud</h2>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Collega il tuo spazio cloud per sincronizzare automaticamente i file dei clienti
            </p>
          </div>
          {serverState?.configured && (
            <span className={`badge ${serverState.enabled ? "badge-completato" : "badge-bozza"}`}>
              {serverState.enabled ? "Attiva" : "Disattiva"}
            </span>
          )}
        </div>

        <form onSubmit={save} className="p-6 space-y-5">
          <div className="p-3 rounded-md text-xs leading-relaxed flex gap-2" style={{ background: "#FFF4E5", color: "#8A4D0F" }}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Come ottenere le credenziali</strong><br />
              Accedi al tuo Aruba Drive → Impostazioni personali → Sicurezza → <em>Password delle app</em> e crea una nuova password app. Utilizzala qui sotto al posto della password personale.
            </div>
          </div>

          <Field
            label="URL Aruba Drive / Nextcloud *"
            value={form.base_url}
            onChange={(v) => setForm({ ...form, base_url: v })}
            placeholder="https://nc-xxxxx.arubadrive.it"
            testid="nc-url"
            required
            helper="URL base (senza /remote.php)"
          />
          <Field
            label="Username *"
            value={form.username}
            onChange={(v) => setForm({ ...form, username: v })}
            placeholder="mario.rossi@azienda.it"
            testid="nc-user"
            required
          />
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              Password app {serverState?.password_set && <span className="text-[color:var(--text-muted)] normal-case ml-1">— presente, lascia vuoto per mantenerla</span>}
            </label>
            <div className="mt-1 relative">
              <KeyRound className="w-4 h-4 absolute left-3 top-2.5" style={{ color: "var(--text-muted)" }} />
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={serverState?.password_set ? "••••••••••" : "Inserisci password app"}
                data-testid="nc-password"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)", background: "#fff" }}
                required={!serverState?.password_set}
              />
            </div>
          </div>

          <Field
            label="Cartella radice"
            value={form.folder_root}
            onChange={(v) => setForm({ ...form, folder_root: v })}
            placeholder="CRM-Solare (opzionale)"
            testid="nc-root"
            helper="Sottocartella dentro la quale creare 'Clienti/'. Lascia vuoto per usare la radice."
          />

          <div className="flex flex-col gap-2 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="w-4 h-4 accent-[var(--primary)]"
                data-testid="nc-enabled"
              />
              Integrazione <strong>attiva</strong>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.auto_upload}
                onChange={(e) => setForm({ ...form, auto_upload: e.target.checked })}
                className="w-4 h-4 accent-[var(--primary)]"
                data-testid="nc-auto"
              />
              <strong>Auto-upload</strong>: ogni file caricato viene pushato subito su Aruba Drive
            </label>
          </div>

          {testResult && (
            <div
              className="p-3 rounded-md text-sm flex items-center gap-2"
              style={testResult.ok ? { background: "#E8F3EB", color: "#1E4620" } : { background: "#FFEBEB", color: "#B30000" }}
              data-testid="test-result"
            >
              {testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {testResult.message}
              {testResult.status ? <span className="text-xs opacity-70 ml-auto">HTTP {testResult.status}</span> : null}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="nc-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salva configurazione
            </button>
            <button
              type="button"
              onClick={test}
              disabled={testing || !serverState?.configured}
              className="btn-secondary"
              data-testid="nc-test"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
              Testa connessione
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 card-shell p-5 text-sm" style={{ color: "var(--text-muted)" }}>
        <div className="font-bold mb-2" style={{ color: "var(--text)", fontFamily: "Manrope" }}>Come funziona</div>
        <ul className="space-y-1 list-disc pl-5 leading-relaxed">
          <li>Ogni cliente può avere una cartella dedicata: <code>Clienti/NomeCognome_CL-00001/</code></li>
          <li>Sottocartelle create automaticamente: <code>Sopralluogo/{"{sezione}"}</code> e <code>Installazione/{"{sezione}"}</code></li>
          <li>Con <strong>Auto-upload</strong> attivo, i file caricati in CRM vengono copiati subito su Aruba Drive</li>
          <li>Dalla scheda di ogni cliente puoi creare manualmente le cartelle e sincronizzare file esistenti</li>
        </ul>
      </div>

      <div className="mt-6">
        <DicoImpresaConfig />
      </div>

      <div className="mt-6">
        <TrasfertaConfig />
      </div>

      <div className="mt-6">
        <EmailArubaConfig />
      </div>

      <div className="mt-6">
        <CheckQualityConfig />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, required, helper, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
        style={{ borderColor: "var(--border)", background: "#fff" }}
      />
      {helper && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{helper}</div>}
    </div>
  );
}
